import React, { useState, useEffect, useRef } from 'react';
import { Form, Row, InputGroup, Button, Table } from 'react-bootstrap';
import { CCol, CFormLabel, CFormFeedback, CFormInput, CButton } from '@coreui/react';
import { useDispatch } from 'react-redux';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import FormWrapper from '../../pagecomponents/Common/FormWrapper';
import { getMasterData, getCustomerData, getCustomerDetail } from '../../redux/slices/CustomerSlice';
import { addJob, editJob } from '../../redux/slices/JobSlice';
import { checkEmpty, toastMessage } from '../../helpers/utility.js';
import useDebouncedValue from '../../helpers/useDebouncedValue';

const INIT_FORM_DATA = {
  client_Id: 1,
  doc_No: '',
  customer_Id: '',
  customer_Name: '',
  job_No: '', // automatic from database
  job_Date: format(new Date(), 'yyyy-MM-dd'),
  job_type_Code: '',
  job_currency_Code: 'QAR',
  rate: '',
  job_Load_No: '',
  payment_Type: '',
  job_Cheque_No: '',
  jobCharge: [], // Array to store job charge data
};

const MASTER_DATA_PARAMS = {
  skip: 0,
  take: 2000,
  type: '',
  sorting: 'asc',
  sorting_key: 'created_at',
  filter: {},
};

const JobAddEdit = ({ onSave, addCustomerModal, mode, data, isSaveDisabled }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState(INIT_FORM_DATA);
  const [masterJobTypeData, setMasterJobTypeData] = useState([]);
  const [masterPaymentModeData, setMasterPaymentModeData] = useState([]);
  const [masterJobchargeData, setMasterJobchargeData] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [docError, setDocError] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const debouncedCustomerSearch = useDebouncedValue(customerSearch, 350);

  // Autocomplete UX helpers
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const blurTimerRef = useRef(null);
  const latestQueryRef = useRef('');

  // Wheel/scroll fix helper
  const handleWheelToPreventNumberScroll = (e) => {
    // Only when the input is focused, prevent wheel from changing value
    if (document.activeElement === e.currentTarget) {
      e.currentTarget.blur();
      // Optional: re-focus without allowing wheel to change value
      setTimeout(() => e?.currentTarget?.focus(), 0);
    }
  };

  useEffect(() => {
    fetchMasterData();
    document.querySelector("input[name='customer_Name']")?.setAttribute('autocomplete', 'off');
  }, [mode, data, dispatch]);

  const fetchMasterData = async () => {
    try {
      const { payload } = await dispatch(getMasterData({ inputData: MASTER_DATA_PARAMS }));

      setMasterPaymentModeData(payload?.payment_mode || []);
      setMasterJobchargeData(payload?.job_charge || []);

      // Initialize job_charge array in formData
      if (mode && data) {
        const initialCharges = (data.jobCharge || []).map((charge, index) => {
          const matchingCharge = payload?.job_charge?.[index];
          return {
            job_charge_Id: charge.job_charge_Id,
            customer_Id: charge.customer_Id,
            job_Id: charge.job_Id,
            charge_Name: checkEmpty(charge.charge_Name) ? matchingCharge?.charge_Name || '' : charge.charge_Name,
            charge_Code: checkEmpty(charge.charge_Code) ? matchingCharge?.charge_Code || '' : charge.charge_Code,
            charge_value: charge.charge_value || 0,
            basis_par: charge.charge_Qty || 0,
            unit_price: charge.charge_Unit_Amount || 0,
          };
        });

        setFormData((prev) => ({
          ...prev,
          jobCharge: initialCharges,
        }));
      } else {
        const initialCharges =
          payload?.job_charge?.map((charge) => {
            return {
              charge_Name: checkEmpty(charge.charge_Name)
                ? data?.jobCharge?.find((c) => c.charge_Code === charge.charge_Code)?.charge_Name || ''
                : charge.charge_Name,
              charge_Code: checkEmpty(charge.charge_Code)
                ? data?.jobCharge?.find((c) => c.charge_Name === charge.charge_Name)?.charge_Code || ''
                : charge.charge_Code,
              charge_value: 0,
              basis_par: 0,
              unit_price: 0,
            };
          }) || [];

        setFormData((prev) => ({ ...prev, jobCharge: initialCharges }));
      }
    } catch (err) {
      toastMessage('error', 'Failed to fetch data');
    }
  };

  useEffect(() => {
    if (mode && data) {
      // keep selected customer so blur won't deselect it
      setSelectedCustomer({
        customer_Id: data.customer_Id,
        customer_Name: data.customer_Name,
      });

      const existingRates = Array.isArray(data?.jobRate) ? data.jobRate : [];
      if (existingRates.length > 0) {
        setMasterJobTypeData(existingRates);
      } else {
      getJobTypeAgaintCustomer(data.customer_Id, data.customer_Name);
      }

      const initialCharges = (data.jobCharge || []).map((charge) => ({
        job_charge_Id: charge.job_charge_Id,
        customer_Id: charge.customer_Id,
        job_Id: charge.job_Id,
        charge_Name: charge.charge_Name,
        charge_Code: charge.charge_Code,
        charge_value: charge.charge_value || 0,
        basis_par: charge.charge_Qty || 0,
        unit_price: charge.charge_Unit_Amount || 0,
      }));

      setFormData((prev) => ({
        ...prev,
        job_Id: data.job_Id || '',
        doc_No: data.doc_No || '',
        customer_Id: data.customer_Id || '',
        customer_Name: data.customer_Name || '',
        job_No: data.job_No || '',
        job_Date: format(new Date(data.job_Date), 'yyyy-MM-dd'),
        job_type_Code: data.job_type_Code || '',
        job_currency_Code: data.job_currency_Code || 'QAR',
        rate: data.rate || '',
        job_Load_No: data.job_Load_No || '',
        payment_Type: data.payment_Type || '',
        job_Cheque_No: data.job_Cheque_No || '',
        jobCharge: initialCharges,
      }));
    }
  }, [mode, data]);

  const getJobTypeAgaintCustomer = async (customer_Id, customer_Name = '') => {
    let params = {
      sorting: 'asc',
      sorting_key: 'created_at',
      skip: 0,
      searchStr: customer_Id ? '' : customer_Name,
      take: 100,
      filter: {
        customer_Id: customer_Id || 0,
        customer_Registration_No: '',
        customer_Code: '',
      },
      useLookupApi: true,
    };
    const { payload } = await dispatch(getCustomerData({ inputData: params }));
    const rows = Array.isArray(payload?.data?.data)
      ? payload.data.data
      : Array.isArray(payload?.data)
        ? payload.data
        : [];

    const selectedCustomerRow = rows.find((row) => Number(row?.customer_Id) === Number(customer_Id));
    const detailCustomerId = Number(selectedCustomerRow?.customer_Id || customer_Id || 0);

    if (detailCustomerId > 0) {
      const detailResp = await dispatch(getCustomerDetail({ inputData: { customer_Id: detailCustomerId } }));
      setMasterJobTypeData(detailResp?.payload?.data?.[0]?.jobRate || []);
      return;
    }

    setMasterJobTypeData([]);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'job_Date') {
      setFormData({
        ...formData,
        [name]: format(new Date(value), 'yyyy-MM-dd'),
      });
    } else if (name === 'job_Load_No') {
      if (/^\d*$/.test(value)) {
        setFormData((prevState) => {
          const rate = parseFloat(prevState.rate || 0);
          const loadNo = parseInt(value || 0, 10) || 0;
          const firstCharge = {
            ...(prevState.jobCharge[0] || {}),
            unit_price: prevState.rate,
            basis_par: value,
            charge_value: (loadNo * rate).toFixed(2),
          };

          return {
            ...prevState,
            [name]: value,
            jobCharge: [firstCharge, ...prevState.jobCharge.slice(1)],
          };
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSave = async () => {
    if (formData.payment_Type === 'cheque' && checkEmpty(formData.job_Cheque_No)) {
      toastMessage('error', 'Enter cheque number');
    } else if (checkEmpty(formData.customer_Id)) {
      toastMessage('error', 'Enter valid customer');
    } else {
      let api = mode ? editJob : addJob;
      const { payload } = await dispatch(api({ inputData: formData }));

      for (let index = 0; index < formData.jobCharge.length; index++) {
        const charge = formData.jobCharge[index];

        if (checkEmpty(charge.charge_Name) || checkEmpty(charge.charge_Code)) {
          const matchingCharge = payload?.job_charge?.[index];

          if (checkEmpty(charge.charge_Name) && matchingCharge) {
            charge.charge_Name = matchingCharge?.charge_Name || '';
          }
          if (checkEmpty(charge.charge_Code) && matchingCharge) {
            charge.charge_Code = matchingCharge?.charge_Code || '';
          }

          if (checkEmpty(charge.charge_Name) || checkEmpty(charge.charge_Code)) {
            toastMessage('error', 'All job charges must have a name and code');
            return;
          }
        }
      }

      if (payload?.status === 'error') {
        if (payload?.msg === 'Document No Already Exists') {
          setDocError(true);
          toastMessage('error', payload.msg || 'An error occurred while saving.');
        } else {
          toastMessage('error', payload.msg || 'An error occurred while saving.');
        }
      } else {
        toastMessage(payload?.status || 'success', payload?.msg || 'Operation successful.');
        onSave();
      }
    }
  };

  const handleOnJobTypeSelect = (e) => {
    const { value } = e.target;
    const selectedJobType = masterJobTypeData.find((jobType) => jobType.job_type_code === value);

    setFormData((prevState) => {
      const nextRate = selectedJobType?.rate || '';
      const loadNo = parseInt(prevState.job_Load_No || 0, 10) || 0;
      const rateNum = parseFloat(nextRate || 0);
      const firstCharge = {
        ...(prevState.jobCharge[0] || {}),
        unit_price: nextRate,
        basis_par: prevState.job_Load_No || 0,
        charge_value: (rateNum * loadNo).toFixed(2),
      };

      return {
        ...prevState,
        job_type_Code: value,
        rate: nextRate,
        jobCharge: [firstCharge, ...prevState.jobCharge.slice(1)],
      };
    });
  };

  // Autocomplete: close on blur, keep selection unless user types something else
  const handleCustomerFocus = () => {
    if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    if (suggestions.length > 0) setShowSuggestions(true);
  };

  const handleCustomerBlur = () => {
    blurTimerRef.current = setTimeout(() => {
      setShowSuggestions(false);
    }, 150);
  };

  useEffect(() => {
    const q = String(debouncedCustomerSearch || '').trim();

    if (!q) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    let isMounted = true;
    latestQueryRef.current = q;

    const fetchSuggestions = async () => {
      try {
        const params = {
          sorting: 'asc',
          sorting_key: 'created_at',
          skip: 0,
          searchStr: q,
          take: 20,
          filter: {
            customer_Id: 0,
            customer_Registration_No: '',
            customer_Code: '',
          },
          useLookupApi: true,
        };

        const { payload } = await dispatch(getCustomerData({ inputData: params }));

        if (!isMounted || latestQueryRef.current !== q) return;

        const rows = Array.isArray(payload?.data?.data)
          ? payload.data.data
          : Array.isArray(payload?.data)
            ? payload.data
            : [];

        const unique = Array.from(new Map(rows.map((r) => [r.customer_Id, r])).values());

        setSuggestions(unique);
        setShowSuggestions(true);
      } catch (err) {
        if (!isMounted) return;
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    fetchSuggestions();

    return () => {
      isMounted = false;
    };
  }, [debouncedCustomerSearch, dispatch]);

  const handleAutoComplete = (e) => {
    const value = e.target.value;
    const q = value.trim();

    setFormData((prev) => {
      const next = { ...prev, customer_Name: value, job_type_Code: '', rate: '' };

      if (selectedCustomer && value === selectedCustomer.customer_Name) {
        next.customer_Id = selectedCustomer.customer_Id;
      } else {
        next.customer_Id = '';
        setSelectedCustomer(null);
        setMasterJobTypeData([]);
      }

      return next;
    });

    setCustomerSearch(value);

    if (!q) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
  };

  const onSelectCustomer = (customerData) => {
    setSelectedCustomer(customerData);

    setFormData((prev) => ({
      ...prev,
      customer_Id: customerData?.customer_Id || '',
      customer_Name: customerData?.customer_Name || '',
    }));

    (async () => {
      const detailAction = await dispatch(
        getCustomerDetail({ inputData: { customer_Id: customerData?.customer_Id || 0 } }),
      );
      setMasterJobTypeData(detailAction?.payload?.data?.[0]?.jobRate || []);
    })();

    setCustomerSearch('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleChargeChange = (index, field, value) => {
    if (value.length > 1 && value.startsWith('0')) {
      value = value.replace(/^0+/, '');
    }

    const validationPattern = field === 'basis_par' ? /^\d*$/ : /^\d*\.?\d{0,2}$/;

    if (validationPattern.test(value)) {
      setFormData((prev) => {
        const updatedJobCharge = [...prev.jobCharge];

        if (!updatedJobCharge[index]) {
          updatedJobCharge[index] = {};
        }

        updatedJobCharge[index] = {
          ...updatedJobCharge[index],
          [field]: value,
        };

        const unitPrice = parseFloat(updatedJobCharge[index].unit_price || 0);
        const basisPer = parseInt(updatedJobCharge[index].basis_par || 0, 10) || 0;

        if (unitPrice && basisPer) {
          updatedJobCharge[index].charge_value = (
            unitPrice * basisPer
          ).toFixed(2);
        } else {
          updatedJobCharge[index].charge_value = 0;
        }

        return { ...prev, jobCharge: updatedJobCharge };
      });
    }
  };

  const calculateTotal = () => {
    const total = formData.jobCharge.reduce((sum, charge) => {
      const amount = parseFloat(charge?.charge_value) || 0;
      return sum + amount;
    }, 0);

    return total.toFixed(2);
  };

  return (
    <FormWrapper onSubmit={handleSave}>
      <Row className="row-xs mb-2">
        <CCol md={3}>
          <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="doc_No">
            Doc No
          </CFormLabel>

          <CFormInput
            type="text"
            id="doc_No"
            name="doc_No"
            value={formData.doc_No}
            onChange={handleInputChange}
            invalid={docError}
            required
          />

          {docError && <div className="text-danger mt-1">Doc No should be unique</div>}
        </CCol>

        <CCol md={5}>
          <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="job_Date">
            Job Date
          </CFormLabel>
          <InputGroup className="input-group reactdate-pic">
            <InputGroup.Text className="input-group-text">
              <i className="typcn typcn-calendar-outline tx-24 lh--9 op-6"></i>
            </InputGroup.Text>
            <DatePicker
              className="form-control"
              name="job_Date"
              id="job_Date"
              selected={formData.job_Date ? new Date(formData.job_Date) : null}
              onChange={(date) => {
                const event = {
                  target: {
                    name: 'job_Date',
                    value: date,
                  },
                };
                handleInputChange(event);
              }}
              dateFormat="yyyy-MM-dd"
            />
          </InputGroup>
          <CFormFeedback invalid>Select Job Date</CFormFeedback>
        </CCol>

        <CCol md={4}>
          <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="customer_Name">
            Search Customer
          </CFormLabel>

          <div className="hsc-wrapper">
            <InputGroup className="input-group">
              <CFormInput
                type="text"
                id="customer_Name"
                name="customer_Name"
                value={formData.customer_Name}
                onChange={handleAutoComplete}
                onFocus={handleCustomerFocus}
                onBlur={handleCustomerBlur}
                required
                autoComplete="off"
              />
              <Button variant="" className="btn btn-primary br-ts-0 br-bs-0" type="button" onClick={addCustomerModal}>
                <i className="fa fa-plus"></i>
              </Button>
            </InputGroup>

            <CFormFeedback invalid>Search or Add Customer</CFormFeedback>

            {showSuggestions && suggestions && suggestions.length > 0 && (
              <div
                className="header-search-container"
                onMouseDown={(e) => e.preventDefault()}
                onPointerDown={(e) => e.preventDefault()}
              >
                <div className="hs">
                  {suggestions.map((suggestion) => (
                    <div
                      key={suggestion.customer_Id}
                      className="main-message-list header-search-element"
                      onPointerDown={(e) => {
                        e.preventDefault();
                        onSelectCustomer(suggestion);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <strong>
                        {suggestion?.customer_Name} - {suggestion?.customer_Code}{' '}
                      </strong>
                      <span>{suggestion?.customer_Email}</span>
                      <span>{suggestion?.customer_Mobile}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CCol>
      </Row>

      <Row className="row-xs mb-2">
        <CCol md={6}>
          <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="job_type_Code">
            Job Type
          </CFormLabel>
          <Form.Select
            className="form-control"
            id="job_type_Code"
            name="job_type_Code"
            value={formData.job_type_Code}
            onChange={handleOnJobTypeSelect}
            required
          >
            <option value="">Select {formData.customer_Id ? 'Job Type' : 'Customer'}</option>
            {masterJobTypeData?.map((option, index) => (
              <option key={index} value={option.job_type_code}>
                {option.job_type_Name}
              </option>
            ))}
          </Form.Select>
          <CFormFeedback invalid>Job Type</CFormFeedback>
        </CCol>

        <CCol md={3}>
          <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="rate">
            Rate
          </CFormLabel>
          <CFormInput type="text" id="rate" name="rate" value={formData.rate} disabled />
        </CCol>

        <CCol md={3}>
          <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="job_Load_No">
            Vehicles
          </CFormLabel>
          {/* FIX: prevent wheel scroll changing value */}
          <CFormInput
            type="number"
            inputMode="numeric"
            step="1"
            id="job_Load_No"
            name="job_Load_No"
            value={formData.job_Load_No}
            onChange={handleInputChange}
            onWheel={handleWheelToPreventNumberScroll}
            required
          />
          <CFormFeedback invalid>No of Vch</CFormFeedback>
        </CCol>
      </Row>

      <Row className="row-xs mb-2">
        <CCol md={4}>
          <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="payment_Type">
            Paid By
          </CFormLabel>
          <Form.Select
            className="form-control"
            id="payment_Type"
            name="payment_Type"
            value={formData.payment_Type}
            onChange={handleInputChange}
            required
          >
            <option value="">Select</option>
            {masterPaymentModeData?.map((mode, index) => (
              <option key={index} value={mode.payment_mode_Code}>
                {mode.payment_mode_Name}
              </option>
            ))}
          </Form.Select>
          <CFormFeedback invalid>Paid By</CFormFeedback>
        </CCol>

        <CCol md={4}>
          {formData.payment_Type && formData.payment_Type === 'cheque' && (
            <>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="job_Cheque_No">
                Cheque No
              </CFormLabel>
              <CFormInput
                type="text"
                id="job_Cheque_No"
                name="job_Cheque_No"
                value={formData.job_Cheque_No}
                onChange={handleInputChange}
                required
              />
              <CFormFeedback invalid>cheque no</CFormFeedback>
            </>
          )}
        </CCol>
      </Row>

      <Row className="row-xs mb-2 pb-3 pt-3">
        <Table className="table table-bordered text-nowrap mb-0">
          <thead>
            <tr>
              <th style={{ width: '30%' }}>Description</th>
              <th style={{ width: '20%' }}>Unit Price</th>
              <th style={{ width: '20%' }}>Basis Per</th>
              <th style={{ width: '25%' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {masterJobchargeData?.map((row, index) => (
              <tr key={index}>
                <td>{row.charge_Name}</td>
                <td>
                  {/* FIX: prevent wheel scroll changing value */}
                  <CFormInput
                    type="number"
                    step="0.01"
                    inputMode="decimal"
                    value={formData.jobCharge[index]?.unit_price || ''}
                    onChange={(e) => handleChargeChange(index, 'unit_price', e.target.value)}
                    onWheel={handleWheelToPreventNumberScroll}
                  />
                </td>
                <td>
                  {/* FIX: prevent wheel scroll changing value */}
                  <CFormInput
                    type="number"
                    step="1"
                    inputMode="numeric"
                    value={formData.jobCharge[index]?.basis_par || ''}
                    onChange={(e) => handleChargeChange(index, 'basis_par', e.target.value)}
                    onWheel={handleWheelToPreventNumberScroll}
                  />
                </td>
                <td>
                  <CFormInput
                    className="form-control-sm"
                    type="text"
                    id={`charge_${index}`}
                    name={`charge_${index}`}
                    value={formData.jobCharge[index]?.charge_value || 0}
                    disabled
                  />
                </td>
              </tr>
            ))}
            <tr>
              <th className="text-end">TOTAL</th>
              <td colSpan="2"></td>
              <td className="text-end">
                <CFormInput
                  type="text"
                  id="charges_total"
                  name="charges_total"
                  value={calculateTotal()}
                  disabled
                  className="text-end"
                />
              </td>
            </tr>
          </tbody>
        </Table>
      </Row>

      <Row className="row-xs mb-2 btn-animation">
        <CCol xs={12} className="mt-10">
          <CButton
            color="primary"
            className={`btn btn-sm float-end ${isSaveDisabled ? 'btn-loaders' : ''}`}
            type="submit"
            disabled={isSaveDisabled}
          >
            {isSaveDisabled ? <span className="loading">Save Changes</span> : 'Save Changes'}
          </CButton>
          <CButton color="secondary" className="btn btn-sm float-end me-2" onClick={onSave} disabled={isSaveDisabled}>
            Close
          </CButton>
        </CCol>
      </Row>
    </FormWrapper>
  );
};

export default JobAddEdit;
